import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { fetchWarehouses } from '@/warehouse/topologyApi'
import { CreateField, CreateModal } from './CreateModal'
import { RecordPicker } from './RecordPicker'
import { createTransfer, fetchStockOf, openReceipt, openUnplannedReceipt } from './warehouseApi'

/**
 * Оформление складских документов из офиса (#94): приёмка и перемещение.
 *
 * Обёртки `POST /warehouse/receipts`, `/receipts/unplanned` и `/transfers` были объявлены и не
 * звались ни одним экраном: офис не мог завести приёмку или перемещение иначе как через API.
 * Здесь только **оформление** — пересчёт строк и проведение остаются на ТСД (#52), а остаток
 * меняется движением, а не формой.
 *
 * Склады берутся справочником топологии (`warehouse.topology:read`); у кого его нет, форма
 * говорит это словами, а не показывает пустой выбор.
 */

type Picked = { id: string; label: string } | null

/** Склады, которые можно выбрать; закрытые не предлагаются. */
function useWarehouses() {
  const query = useQuery({
    queryKey: ['topology', 'warehouses'],
    queryFn: ({ signal }) => fetchWarehouses(signal),
    retry: false,
  })
  const denied = query.error instanceof ApiError && query.error.isForbidden
  const options = (query.data?.warehouses ?? []).filter((warehouse) => warehouse.active)
  return { options, ready: query.data !== undefined, denied }
}

function positive(raw: string): number | null {
  const value = Number(raw.replace(',', '.'))
  return Number.isFinite(value) && value > 0 ? value : null
}

// ------------------------------------------------------------------ приёмка

interface ReceiptLineDraft {
  key: number
  product: Picked
  quantity: string
  unitCost: string
}

/**
 * Приёмка (`warehouse.receipt:create`).
 *
 * Плановая несёт ожидаемое — строки с количеством: расхождение надо увидеть у ворот, а не
 * восстанавливать потом. Внеплановая (машина без заказа) строк **не несёт** намеренно: их
 * наберёт кладовщик сканером, и всё доехавшее прочитается как расхождение в плюс. Цена строки
 * необязательна, но без неё приход не создаст слоя себестоимости — это говорит подсказка.
 */
export function ReceiptCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const warehouses = useWarehouses()
  const [warehouseId, setWarehouseId] = useState('')
  const [supplier, setSupplier] = useState<Picked>(null)
  const [planned, setPlanned] = useState(true)
  const [lines, setLines] = useState<ReceiptLineDraft[]>([{ key: 1, product: null, quantity: '', unitCost: '' }])
  const [notice, setNotice] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      planned
        ? openReceipt({
            warehouseId,
            supplierId: supplier?.id ?? null,
            lines: lines.map((line) => ({
              productId: line.product!.id,
              quantity: positive(line.quantity)!,
              unitCost: line.unitCost.trim() === '' ? null : Number(line.unitCost.replace(',', '.')),
            })),
          })
        : openUnplannedReceipt({ warehouseId, supplierId: supplier?.id ?? null }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'warehouse.receipt'] })
      navigate(`/receipts/${created.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'warehouse.receipt', 'create')) return <AccessDenied />

  const incomplete = lines.some((line) => line.product === null || positive(line.quantity) === null)
  const blockedReason =
    warehouseId === ''
      ? t('whdoc.create.needWarehouse')
      : planned && incomplete
        ? t('whdoc.create.needLines')
        : null
  const patch = (key: number, change: Partial<ReceiptLineDraft>) =>
    setLines((all) => all.map((line) => (line.key === key ? { ...line, ...change } : line)))

  return (
    <CreateModal
      overline={t('nav.receipts')}
      title={t('whdoc.receipt.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('whdoc.receipt.submit')}
      onCancel={() => navigate('/receipts')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <div className="form-grid">
        <CreateField id="receipt-warehouse" label={t('column.warehouse_id')} required span
          help={warehouses.denied ? t('whdoc.create.noTopology') : undefined}>
          <select id="receipt-warehouse" className="input" value={warehouseId} disabled={!warehouses.ready}
            onChange={(event) => setWarehouseId(event.target.value)}>
            <option value="">{t('whdoc.create.pickWarehouse')}</option>
            {warehouses.options.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </CreateField>
        <CreateField id="receipt-supplier" label={t('column.supplier_id')} span>
          <RecordPicker id="receipt-supplier" entity="sales.counterparty" searchColumn="name" extraColumn="inn"
            value={supplier} onChange={setSupplier} />
        </CreateField>
        <CreateField label={t('whdoc.receipt.kind')} span help={planned ? undefined : t('whdoc.receipt.unplannedHelp')}>
          <div className="row" role="radiogroup" aria-label={t('whdoc.receipt.kind')} style={{ gap: 12 }}>
            <label className="row" style={{ gap: 6 }}>
              <input type="radio" name="receipt-kind" checked={planned} onChange={() => setPlanned(true)} />
              {t('whdoc.receipt.planned')}
            </label>
            <label className="row" style={{ gap: 6 }}>
              <input type="radio" name="receipt-kind" checked={!planned} onChange={() => setPlanned(false)} />
              {t('whdoc.receipt.unplanned')}
            </label>
          </div>
        </CreateField>
      </div>

      {planned && (
        <fieldset className="stack" style={{ gap: 8, border: 0, padding: 0, margin: '12px 0 0' }}>
          <legend className="label">{t('whdoc.lines')}</legend>
          {lines.map((line, index) => (
            <div key={line.key} className="form-grid" role="group" aria-label={t('whdoc.line', { n: index + 1 })}>
              <CreateField id={`receipt-product-${line.key}`} label={t('column.product_id')} required span>
                <RecordPicker id={`receipt-product-${line.key}`} entity="sales.product" searchColumn="name" extraColumn="sku"
                  value={line.product} onChange={(product) => patch(line.key, { product })} />
              </CreateField>
              <CreateField id={`receipt-qty-${line.key}`} label={t('whdoc.quantity')} required>
                <input id={`receipt-qty-${line.key}`} className="input" inputMode="decimal" value={line.quantity}
                  onChange={(event) => patch(line.key, { quantity: event.target.value })} />
              </CreateField>
              <CreateField id={`receipt-cost-${line.key}`} label={t('whdoc.unitCost')} help={t('whdoc.unitCostHelp')}>
                <input id={`receipt-cost-${line.key}`} className="input" inputMode="decimal" value={line.unitCost}
                  onChange={(event) => patch(line.key, { unitCost: event.target.value })} />
              </CreateField>
              {lines.length > 1 && (
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => setLines((all) => all.filter((other) => other.key !== line.key))}>
                  {t('whdoc.removeLine')}
                </button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
            onClick={() => setLines((all) => [...all, { key: Math.max(...all.map((l) => l.key)) + 1, product: null, quantity: '', unitCost: '' }])}>
            {t('whdoc.addLine')}
          </button>
        </fieldset>
      )}
    </CreateModal>
  )
}

// ------------------------------------------------------------------ перемещение

interface TransferLineDraft {
  key: number
  product: Picked
  stockId: string
  quantity: string
}

/**
 * Перемещение между складами (`warehouse.transfer:create`).
 *
 * Строка указывает **ячейку отправителя**, откуда товар возьмут: отправка списывает с остатка
 * по ячейке, и строка без неё упала бы при отправке ошибкой «нет товара в ячейке null» —
 * то есть в момент, когда машина уже у ворот. Ячейка выбирается из остатка товара на складе
 * отправителя, со свободным количеством рядом. Черновик ничего не двигает: товар уходит с
 * остатка при отправке.
 */
export function TransferCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const warehouses = useWarehouses()
  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')
  const [lines, setLines] = useState<TransferLineDraft[]>([{ key: 1, product: null, stockId: '', quantity: '' }])
  const [notice, setNotice] = useState<string | null>(null)

  const stockByKey = useQuery({
    queryKey: ['transfer-stock', fromId, lines.map((line) => line.product?.id ?? '').join()],
    enabled: fromId !== '' && lines.some((line) => line.product !== null),
    queryFn: async ({ signal }) => {
      const products = [...new Set(lines.map((line) => line.product?.id).filter((id): id is string => Boolean(id)))]
      const rows = await Promise.all(products.map((id) => fetchStockOf(id, fromId, signal)))
      return Object.fromEntries(products.map((id, index) => [id, rows[index] ?? []]))
    },
    retry: false,
  })

  const save = useMutation({
    mutationFn: () => {
      const stock = stockByKey.data ?? {}
      return createTransfer({
        fromWarehouseId: fromId,
        toWarehouseId: toId,
        lines: lines.map((line) => {
          const row = (stock[line.product!.id] ?? []).find((candidate) => candidate.id === line.stockId)!
          return { productId: line.product!.id, batchId: row.batchId, fromCellId: row.cellId, quantity: positive(line.quantity)! }
        }),
      })
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'warehouse.transfer'] })
      navigate(`/transfers/${created.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'warehouse.transfer', 'create')) return <AccessDenied />

  const patch = (key: number, change: Partial<TransferLineDraft>) =>
    setLines((all) => all.map((line) => (line.key === key ? { ...line, ...change } : line)))
  const incomplete = lines.some((line) => line.product === null || line.stockId === '' || positive(line.quantity) === null)
  const blockedReason =
    fromId === '' || toId === ''
      ? t('whdoc.transfer.needWarehouses')
      : fromId === toId
        ? t('whdoc.transfer.sameWarehouse')
        : incomplete
          ? t('whdoc.transfer.needLines')
          : null

  return (
    <CreateModal
      overline={t('nav.transfers')}
      title={t('whdoc.transfer.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('whdoc.transfer.submit')}
      onCancel={() => navigate('/transfers')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <div className="form-grid">
        <CreateField id="transfer-from" label={t('column.from_warehouse_id')} required
          help={warehouses.denied ? t('whdoc.create.noTopology') : undefined}>
          <select id="transfer-from" className="input" value={fromId} disabled={!warehouses.ready}
            onChange={(event) => {
              setFromId(event.target.value)
              setLines((all) => all.map((line) => ({ ...line, stockId: '' })))
            }}>
            <option value="">{t('whdoc.create.pickWarehouse')}</option>
            {warehouses.options.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </CreateField>
        <CreateField id="transfer-to" label={t('column.to_warehouse_id')} required>
          <select id="transfer-to" className="input" value={toId} disabled={!warehouses.ready}
            onChange={(event) => setToId(event.target.value)}>
            <option value="">{t('whdoc.create.pickWarehouse')}</option>
            {warehouses.options.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </CreateField>
      </div>

      <fieldset className="stack" style={{ gap: 8, border: 0, padding: 0, margin: '12px 0 0' }}>
        <legend className="label">{t('whdoc.lines')}</legend>
        {lines.map((line, index) => {
          const rows = line.product ? (stockByKey.data?.[line.product.id] ?? []) : []
          const chosen = rows.find((row) => row.id === line.stockId)
          return (
            <div key={line.key} className="form-grid" role="group" aria-label={t('whdoc.line', { n: index + 1 })}>
              <CreateField id={`transfer-product-${line.key}`} label={t('column.product_id')} required span>
                <RecordPicker id={`transfer-product-${line.key}`} entity="sales.product" searchColumn="name" extraColumn="sku"
                  value={line.product} onChange={(product) => patch(line.key, { product, stockId: '' })} />
              </CreateField>
              <CreateField id={`transfer-cell-${line.key}`} label={t('whdoc.transfer.fromCell')} required
                help={line.product && fromId && stockByKey.isSuccess && rows.length === 0 ? t('whdoc.transfer.noStock') : undefined}>
                <select id={`transfer-cell-${line.key}`} className="input" value={line.stockId}
                  disabled={rows.length === 0} onChange={(event) => patch(line.key, { stockId: event.target.value })}>
                  <option value="">{t('whdoc.transfer.pickCell')}</option>
                  {rows.map((row) => (
                    <option key={row.id} value={row.id}>
                      {t('whdoc.transfer.cellOption', { cell: row.cellCode ?? '—', available: row.available })}
                    </option>
                  ))}
                </select>
              </CreateField>
              <CreateField id={`transfer-qty-${line.key}`} label={t('whdoc.quantity')} required
                help={chosen && positive(line.quantity) !== null && positive(line.quantity)! > chosen.available
                  ? t('whdoc.transfer.overAvailable', { available: chosen.available }) : undefined}>
                <input id={`transfer-qty-${line.key}`} className="input" inputMode="decimal" value={line.quantity}
                  onChange={(event) => patch(line.key, { quantity: event.target.value })} />
              </CreateField>
              {lines.length > 1 && (
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => setLines((all) => all.filter((other) => other.key !== line.key))}>
                  {t('whdoc.removeLine')}
                </button>
              )}
            </div>
          )
        })}
        <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
          onClick={() => setLines((all) => [...all, { key: Math.max(...all.map((l) => l.key)) + 1, product: null, stockId: '', quantity: '' }])}>
          {t('whdoc.addLine')}
        </button>
      </fieldset>
    </CreateModal>
  )
}
